import { Icon, Spinner, TableColumn, Typography } from '@scale/scaleui';
import React, { useCallback } from 'react';
import { useQuery } from 'react-query';
import { Link, useHistory } from 'react-router-dom';
import { Routes, buildRoute } from 'react-scale/components/ExpertManagement/urls';
import SelectScaler from 'react-scale/corp/components/UI/SelectScaler';
import { LoadingTable } from '../LoadingTable';
import { fetchManagerHierarchyData, fetchManagerLastAttemptedProjects } from '../api';
import NestedTable from './NestedTable';
import { StatisticCard } from '../StatisticCard';
import { communityGlobalStatsCards, podStatsCards } from '../cards';
import { useFetchCommunityGlobalStats } from '../../hooks/queries/useFetchCommunityGlobalStats';
import { useFetchPodStats } from '../../hooks/queries/useFetchPodStats';

export const formatName = (worker: any) => `${worker.firstName}`;

const NameCell: React.FC<any> = props => {
  return (
    <Link
      to={buildRoute(Routes.ExpertManagement, { user: props.row.original.worker._id })}
      className="flex flex-col"
    >
      <div>{formatName(props.row.original.worker)}</div>
      <div className="text-xs">{props.row.original.worker.email}</div>
    </Link>
  );
};

const TLCColumns: TableColumn[] = [
  {
    id: 'name',
    header: 'TLC Name',
    cell: NameCell,
  },
  {
    id: 'numManaged',
    header: 'People Managed',
    cell: function NumSquads(props: any) {
      return props.row.original.workers.length;
    },
  },
  {
    id: 'podType',
    header: 'Pod Type',
    cell: function PodType(props: any) {
      return props.row.original.teamLeadPodType.name;
    },
  },
];

const TLColumns: TableColumn[] = [
  {
    id: 'name',
    header: 'TL Name',
    cell: NameCell,
  },
  {
    id: 'role',
    header: 'Role',
    cell: function Role(props: any) {
      return props.row.original.role === 'Manager' ? 'TL' : 'Expert';
    },
  },
  {
    id: 'numManaged',
    header: 'People Managed',
    cell: function NumExperts(props: any) {
      return props.row.original.teamSize;
    },
  },
  {
    id: 'projects',
    header: 'Projects',
    cell: function Projects(props: any) {
      const { data, isLoading } = useQuery(
        ['managerProjects', props.row.original._id],
        () => fetchManagerLastAttemptedProjects(props.row.original.worker._id),
        {
          staleTime: 60 * 60 * 1000,
        },
      );
      return isLoading ? (
        <Spinner size="sm" />
      ) : data?.length ? (
        <div className="flex flex-col max-h-[50px] overflow-auto">
          {(data ?? []).map((project: any) => (
            <div key={project._id}>{project.name}</div>
          ))}
        </div>
      ) : (
        'N/A'
      );
    },
  },
  {
    id: 'workerTeams',
    header: 'WorkerTeams',
    cell: function WTs() {
      return 'N/A';
    },
  },
  {
    id: 'podType',
    header: 'Pod Type',
    cell: function PodType(props: any) {
      return props.row.original.teamLeadPodType.name;
    },
  },
];

interface PageProps {}

export const TeamLeadOverview: React.FC<PageProps> = () => {
  const history = useHistory();
  const { data, isLoading } = useQuery(
    ['managerHierarchyData'],
    () => fetchManagerHierarchyData(),
    {
      staleTime: 60 * 60 * 1000,
    },
  );
  const handleSelectUser = useCallback(
    (user: any) => {
      const userId = user.value;
      history.push(buildRoute(Routes.ExpertManagement, { user: userId }));
    },
    [history],
  );
  const squads = Object.values(data ?? {}).filter(
    (pod: any) => pod[0].teamLeadPodType.name !== 'Testing',
  );

  const { data: globalStats, isLoading: isLoadingGlobalStats } = useFetchCommunityGlobalStats();
  const statsCards = communityGlobalStatsCards(globalStats);

  return (
    <div className="flex flex-col">
      <Typography.H3 className="m-4">Team Lead Dashboard</Typography.H3>
      <div className="mb-4 ml-4">
        {isLoadingGlobalStats || !globalStats ? (
          <div className="flex gap-2 items-center">
            <div>Loading global stats...</div>
            <Spinner size="sm" />
          </div>
        ) : (
          <div className="flex gap-4 w-full flex-wrap">
            {statsCards.map(card => (
              <StatisticCard key={`tld-global-stats-${card.title}`} {...card} />
            ))}
          </div>
        )}
      </div>
      <div className="mx-4 w-[60%] min-w-[600px]">
        <SelectScaler
          placeholder="Search for a contributor or team lead to view their profile"
          onChange={handleSelectUser}
          filterOption={() => true}
        />
      </div>
      <div className="mt-4 ml-4 mb-4 flex gap-4">
        {isLoading || !data ? (
          <div className="w-[60%]">
            <LoadingTable columns={TLCColumns} numRows={6} rowHeight={2} />
          </div>
        ) : (
          <div className="flex flex-row gap-4 w-full">
            <div className="flex flex-col gap-4 w-[50%]">
              {squads
                .filter((_pod: object[], i: number) => i % 2 === 0)
                .map((pod: object[]) => (
                  <PodSection key={(pod[0] as any).teamLeadPodType.id} pod={pod} />
                ))}
            </div>
            <div className="flex flex-col gap-4 w-[48%]">
              {squads
                .filter((_pod: object[], i: number) => i % 2 === 1)
                .map((pod: object[]) => (
                  <PodSection key={(pod[0] as any).teamLeadPodType.id} pod={pod} />
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface PodSectionProps {
  pod: any[];
}

const PodSection: React.FC<PodSectionProps> = ({ pod }) => {
  const podDetails = (pod[0] as any)?.teamLeadPodType;
  const podName = podDetails?.name;
  const podId = podDetails?._id ?? '';
  const { loading: isLoading, value: data } = useFetchPodStats(podName, podId);
  const statsCards = podStatsCards(data);

  return (
    <div className="flex flex-col gap-2">
      <Typography.H4 className="flex items-center">
        {(pod[0] as any).teamLeadPodType.name}
        <Link
          to={buildRoute(Routes.ExpertManagement, {
            podType: (pod[0] as any).teamLeadPodType._id,
          })}
        >
          <Icon icon="external-link" variant="primary" />
        </Link>
        {isLoading && <Spinner size="sm" />}
      </Typography.H4>
      {!isLoading && data && (
        <div className="flex gap-1">
          {statsCards.map(card => (
            <StatisticCard key={`tld-pod-stats-${card.title}`} {...card} />
          ))}
        </div>
      )}
      <NestedTable
        data={pod}
        columns={TLCColumns}
        subtableField="workers"
        subtableColumns={TLColumns}
        stripedRows
      />
    </div>
  );
};
